import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axiosInstance from "../../../utils/axios";
import { FaFileImage, FaSpinner } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import uploadToCloudinary from "../../../utils/cloudinary";
import CampaignCategoryDropdown from "../CampaignCategoryDropdown";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const SpecialCampaignModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = {},
  isEditMode,
  isSubmitting,
}) => {
  const defaultForm = {
    name: "",
    description: "",
    isSignificant: true,
    goal: 0,
    raised: 0,
    images: [],
    logo: "",
  };
  const itineraryForm = [
    {
      day: "",
      heading: "",
      content: "",
      infoImages: [],
      videoURL: [""],
    },
  ];

  // helper id generator for placeholders
  const makeId = () =>
    `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  const [formData, setFormData] = useState(defaultForm);
  //   const [categories, setCategories] = useState([]);
  const [imageArray, setImageArray] = useState([]); // objects: { localId?, file?, preview?, isUploading?, url? }
  const [uploadedImageUrls, setUploadedImageUrls] = useState([]);
  const [itinerary, setItinerary] = useState(itineraryForm);
  const [anyImageUploading, setAnyImageUploading] = useState(false);
  const [logoObj, setLogoObj] = useState(null);
  // { localId, file, preview, isUploading, url }

  const [logoUrl, setLogoUrl] = useState("");

  //FETCH ALL THE CATEGORIES TO SHOW ON  DROPDOWN
  //   useEffect(() => {
  //     const fetchCategories = async () => {
  //       const res = await axiosInstance.get(
  //         "/campaign-categories/all-categories",
  //       );
  //       setCategories(res.data);
  //     };
  //     fetchCategories();
  //   }, []);

  // TO PREFILL THE EXISTING DATA IN CASE OF EDITING ,
  // ALSO ENSURE THAT THE INFOIMAGES IS AN ARRAY OF OBJ WHICH CONTAINS A url PROPERTY NOT DIRECTLY url
  useEffect(() => {
    if (!isEditMode || !initialData) {
      setFormData(defaultForm);
      setItinerary(itineraryForm);
      setImageArray([]);
      setUploadedImageUrls([]);
      setLogoObj(null);
      setLogoUrl("");
      return;
    }

    const initializeEditMode = () => {
      const formattedItinerary =
        initialData.itinerary?.map((item, index) => ({
          ...item,
          day:
            item.day && item.day.trim() !== "" ? item.day : `Day ${index + 1}`,
          infoImages: Array.isArray(item.infoImages)
            ? item.infoImages.map((img) =>
                typeof img === "string" ? { url: img } : img,
              )
            : [],
          videoURL: Array.isArray(item.videoURL)
            ? item.videoURL
            : item.videoURL
              ? [item.videoURL]
              : [""],
        })) || itineraryForm;

      const formattedImages = Array.isArray(initialData.images)
        ? initialData.images.map((url) => ({ url }))
        : [];

      setLogoUrl(initialData.logo || "");

      setFormData({
        ...defaultForm,
        ...initialData,
      });
      setItinerary(formattedItinerary);
      setImageArray(formattedImages);
      setUploadedImageUrls(initialData.images || []);
      setLogoObj(
        initialData.logo ? { localId: makeId(), url: initialData.logo } : null,
      );
      setLogoUrl(initialData.logo || "");
    };
    initializeEditMode();
  }, [initialData, isEditMode]);

  // Prevent background scroll when modal open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // handle field change
  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // ---- FIXED: campaign images upload (stable matching with localId, append urls) ----
  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // create placeholders with localId
    const placeholders = files.map((file) => ({
      localId: makeId(),
      file,
      preview: URL.createObjectURL(file),
      isUploading: true,
      url: null,
    }));

    // append placeholders immediately for preview
    setImageArray((prev) => [...prev, ...placeholders]);

    const uploadedUrlsThisBatch = [];

    // upload sequentially and update placeholder by localId
    for (const ph of placeholders) {
      try {
        setAnyImageUploading(true);
        const uploadedUrl = await uploadToCloudinary(ph.file);

        // update the specific placeholder by localId
        setImageArray((prev) =>
          prev.map((item) =>
            item.localId === ph.localId
              ? { ...item, url: uploadedUrl, isUploading: false }
              : item,
          ),
        );

        uploadedUrlsThisBatch.push(uploadedUrl);
      } catch (err) {
        console.error("Upload failed for campaign image:", err);
        // remove the failed placeholder so UI doesn't keep a broken preview
        setImageArray((prev) =>
          prev.filter((item) => item.localId !== ph.localId),
        );
      } finally {
        setAnyImageUploading(false);
      }
    }

    // append new uploaded urls to existing ones (avoid overwriting previous batches)
    setUploadedImageUrls((prev = []) => {
      // merge and avoid exact duplicates
      const merged = [...prev];
      for (const u of uploadedUrlsThisBatch) {
        if (!merged.includes(u)) merged.push(u);
      }
      return merged;
    });

    // allow re-selecting same files
    e.target.value = null;
  };

  // ---- FIXED: remove campaign image (revoke preview + remove single occurrence from uploadedImageUrls) ----
  const removeImage = (index) => {
    setImageArray((prev) => {
      const updated = [...prev];
      if (index < 0 || index >= updated.length) return prev;
      const [removed] = updated.splice(index, 1); // removed is the item object

      // revoke preview if we created one
      if (removed?.preview) {
        try {
          URL.revokeObjectURL(removed.preview);
        } catch (e) {
          /* ignore */
        }
      }

      // remove only first occurrence of this URL from uploadedImageUrls (do not remove duplicates globally)
      if (removed?.url) {
        setUploadedImageUrls((prevUrls = []) => {
          const idx = prevUrls.findIndex((u) => u === removed.url);
          if (idx === -1) return prevUrls;
          const copy = [...prevUrls];
          copy.splice(idx, 1); // remove only one occurrence
          return copy;
        });
      }

      return updated;
    });
  };

  //Itinerary change
  const handleItineraryChange = (e, index) => {
    const { name, value } = e.target;

    setItinerary((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [name]: value } : item)),
    );
  };
  //add itinerary
  const addMoreItinerary = () => {
    setItinerary((prev) => [
      ...prev,
      {
        day: `Day ${prev.length + 1}`,
        heading: "",
        content: "",
        infoImages: [],
        videoURL: [""],
      },
    ]);
  };

  //remove itinerary
  const removeItinerary = (index) => {
    setItinerary((prev) => prev.filter((_, i) => i !== index));
  };

  //ititnerary image
  const handleItineraryImageChange = async (e, index) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const placeholders = files.map((file) => ({
      localId: makeId(),
      file,
      preview: URL.createObjectURL(file),
      isUploading: true,
      url: null,
    }));

    // Add placeholders
    setItinerary((prev) => {
      const updated = [...prev];
      const existingImages = updated[index]?.infoImages || [];

      // dedupe by file name+size for new files vs existing file objects
      const existingKeys = new Set(
        existingImages.map((img) =>
          img.file
            ? img.file.name + img.file.size
            : img.url
              ? `url__${img.url}`
              : "",
        ),
      );

      const filteredPlaceholders = placeholders.filter(
        (ph) => !existingKeys.has(ph.file.name + ph.file.size),
      );

      const newInfoImages = [...existingImages, ...filteredPlaceholders];
      updated[index] = { ...updated[index], infoImages: newInfoImages };
      return updated;
    });

    for (const ph of placeholders) {
      try {
        setAnyImageUploading(true);
        const uploadedUrl = await uploadToCloudinary(ph.file);

        setItinerary((prev) => {
          const updated = [...prev];
          const imgs = updated[index].infoImages;
          const imgIndex = imgs.findIndex((img) => img.localId === ph.localId);

          if (imgIndex !== -1) {
            imgs[imgIndex] = {
              ...imgs[imgIndex],
              url: uploadedUrl,
              isUploading: false,
            };
          }
          updated[index] = { ...updated[index], infoImages: imgs };
          return updated;
        });
      } catch (err) {
        console.error("Upload failed for itinerary image:", err);
        // remove placeholder on failure
        setItinerary((prev) => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            infoImages: (updated[index].infoImages || []).filter(
              (img) => img.localId !== ph.localId,
            ),
          };
          return updated;
        });
      } finally {
        setAnyImageUploading(false);
      }
    }

    e.target.value = null;
  };
  // REMOVE Itinerary IMAGE (revokes preview if present)
  const removeItineraryImage = (itineraryIndex, imageIndex) => {
    setItinerary((prev) => {
      const updated = [...prev];
      const imgs = updated[itineraryIndex]?.infoImages || [];
      if (imageIndex < 0 || imageIndex >= imgs.length) return prev;
      const [removed] = imgs.splice(imageIndex, 1);
      // revoke preview URL if any
      if (removed?.preview) {
        try {
          URL.revokeObjectURL(removed.preview);
        } catch (e) {
          /* ignore */
        }
      }
      updated[itineraryIndex] = {
        ...updated[itineraryIndex],
        infoImages: imgs,
      };
      return updated;
    });
  };

  const handleVideoChange = (e, index, vidIndex) => {
    const value = e.target.value;

    setItinerary((prev) => {
      const updated = [...prev];
      updated[index].videoURL[vidIndex] = value;
      return updated;
    });
  };
  const addVideoField = (index) => {
    setItinerary((prev) => {
      const updated = [...prev];
      const current = updated[index].videoURL;

      // prevent accidental double add
      if (current[current.length - 1] === "") return prev;

      updated[index].videoURL = [...current, ""];
      return updated;
    });
  };

  const removeVideoField = (index, vidIndex) => {
    setItinerary((prev) => {
      const updated = [...prev];
      updated[index].videoURL = updated[index].videoURL.filter(
        (_, i) => i !== vidIndex,
      );
      return updated;
    });
  };

  /*Logo handle */

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const newLogo = {
      localId: makeId(),
      file,
      preview: URL.createObjectURL(file),
      isUploading: true,
      url: null,
    };

    setLogoObj(newLogo);

    try {
      setAnyImageUploading(true);

      const uploadedUrl = await uploadToCloudinary(file);

      setLogoObj((prev) =>
        prev?.localId === newLogo.localId
          ? { ...prev, url: uploadedUrl, isUploading: false }
          : prev,
      );

      setLogoUrl(uploadedUrl);
    } catch (err) {
      console.error("Logo upload failed:", err);
      setLogoObj(null);
    } finally {
      setAnyImageUploading(false);
    }

    e.target.value = null;
  };

  const removeLogo = () => {
    if (logoObj?.preview) {
      try {
        URL.revokeObjectURL(logoObj.preview);
      } catch (e) {}
    }

    setLogoObj(null);
    setLogoUrl("");
  };

  // handle submit
  const handleSubmit = (e) => {
    e.preventDefault();

    const formattedItinerary = itinerary.map((item) => ({
      day: item.day,
      heading: item.heading,
      content: item.content,
      videoURL: item.videoURL.filter((v) => v),
      infoImages: item.infoImages.map((img) => img.url || img),
    }));

    let isoDate = null;
    if (formData.date) {
      const date = new Date(`${formData.date}T10:00:00`);
      if (!isNaN(date)) {
        isoDate = date.toISOString();
      }
    }

    const finalPayload = {
      name: formData.name,
      description: formData.description,
      isSignificant: formData.isSignificant,
      images: uploadedImageUrls,
      itinerary: formattedItinerary,
      logo: logoUrl,
    };

    onSubmit(finalPayload);
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl px-6 pb-6 rounded-lg shadow-xl relative max-h-[90vh] overflow-y-auto">
        {/* Sticky header with close button */}
        <div className="sticky top-0 bg-white z-20 py-4">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="absolute top-4 right-3 text-gray-700 flex items-center justify-center z-30"
          >
            <MdClose className="w-8 h-8 " />
          </button>
          <h2 className="text-2xl font-bold mb-4">
            {isEditMode ? "Edit Campaign" : "Add New Campaign"}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* NAME */}
          <div>
            <label className="block font-medium mb-1">Campaign Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border-2 rounded outline-primaryColor/30"
              required
            />
          </div>

          {/* DESCRIPTION */}
          <div>
            <label className="block font-medium mb-1">Description</label>
            <ReactQuill
              value={formData.description}
              onChange={(value) =>
                setFormData({ ...formData, description: value })
              }
              className="bg-white custom-quill"
            />
          </div>

          {/* CATEGORY */}

          {/* <div className="flex gap-10">
           
            <div className="relative w-full">
              <label className="block font-medium mb-1">Category</label>
              <CampaignCategoryDropdown
                categories={categories}
                value={formData.category}
                onChange={(value) =>
                  setFormData({ ...formData, category: value })
                }
              />
            </div> */}

          {/* <div className="relative w-full mt-8">
              <label className="flex items-center gap-2 font-medium mb-1">
                <input
                  type="checkbox"
                  name="isSignificant"
                  checked={formData.isSignificant}
                  onChange={handleChange}
                  className="w-4 h-4 accent-primaryColor"
                />
                Is Significant
              </label>
            </div>
          </div> */}

          {/*DATE AND LOCATION */}

          {/* <div className="w-full flex flex-col">
            <label
              htmlFor="campaign-date-field"
              className="block font-medium mb-1"
            >
              Date
            </label>
            <input
              required
              type="date"
              name="date"
              id="campaign-date-field"
              value={formData.date?.slice(0, 10) || ""}
              onChange={handleChange}
              onFocus={(e) => e.target.showPicker()}
              className="w-full px-3 py-2 border-2 rounded outline-primaryColor/30"
            />
          </div> */}

          {/* RAISED AND GOAL */}
          <div className="flex w-full sm:justify-between gap-x-4 items-center flex-col sm:flex-row gap-y-2">
            {/* <div className="w-full">
              <label className="block font-medium mb-1">Goal Amount</label>
              <input
                type="number"
                name="goal"
                value={formData.goal}
                onChange={handleChange}
                className="w-full px-3 py-2 border-2 rounded outline-primaryColor/30"
              />
            </div> */}
            {/* <div className="w-full">
              <div>
                <label className="block font-medium mb-1">
                  Campaign Video URL{" "}
                  <span className="text-xs text-primaryBtnColor">
                    (Optional)
                  </span>
                </label>
                <input
                  type="text"
                  name="videoURL"
                  value={formData.videoURL || ""}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border-2 rounded outline-primaryColor/30"
                />
              </div>
            </div> */}
          </div>

          {/* LOGO UPLOAD */}
          <div>
            <label className="block font-medium mb-1">Campaign Logo</label>

            <div className="flex items-start gap-4">
              <button
                type="button"
                onClick={() => document.getElementById("logo-upload")?.click()}
                className="flex flex-col items-center justify-center w-32 h-24 border-2 border-dashed border-gray-300 rounded-lg hover:border-primaryColor"
              >
                <FaFileImage className="text-2xl text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">
                  {logoObj ? "Change Logo" : "Upload Logo"}
                </span>
              </button>

              <input
                id="logo-upload"
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
                className="hidden"
              />

              {logoObj && (
                <div className="relative w-32 h-24">
                  <img
                    src={logoObj.preview || logoObj.url}
                    alt="logo"
                    className="w-full h-full object-cover rounded border"
                  />

                  {logoObj.isUploading && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <FaSpinner className="text-white animate-spin" />
                    </div>
                  )}

                  {/* REMOVE */}
                  <button
                    type="button"
                    onClick={removeLogo}
                    className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* IMAGES */}
          <div>
            <label className="block font-medium mb-1">
              Campaign Images{" "}
              <span className="text-xs text-amber-600">(Max size 10mb)</span>
              <span className="text-xs text-primaryBtnColor">(Optional)</span>
            </label>
            <div className="flex items-start gap-4">
              <button
                type="button"
                onClick={() =>
                  document.getElementById("image-upload-campaign")?.click()
                }
                className="flex flex-col items-center justify-center w-32 h-24 border-2 border-dashed border-gray-300 rounded-lg hover:border-primaryColor transition-colors"
              >
                <FaFileImage className="text-2xl text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">
                  {imageArray.length > 0 ? "Add More" : "Upload Images"}
                </span>
              </button>
              <input
                id="image-upload-campaign"
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                className="hidden"
              />

              {imageArray.length > 0 && (
                <div className="flex-1 overflow-x-auto pb-2">
                  <div className="flex gap-3">
                    {imageArray.map((image, index) => (
                      <div
                        key={image.localId || image.url || index}
                        className="relative flex-shrink-0 w-32 h-24"
                      >
                        <img
                          src={image.preview || image.url || image}
                          alt={`Upload ${index}`}
                          className="w-full h-full object-cover rounded border"
                        />
                        {image.isUploading && (
                          <div className="absolute inset-0 bg-black bg-opacity-40 flex justify-center items-center z-10">
                            <FaSpinner className="text-white animate-spin" />
                          </div>
                        )}
                        {/* REMOVE BUTTON */}
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-5 h-5 pb-0.5 flex items-center justify-center shadow-md hover:bg-red-700 transition-colors"
                        >
                          <span>x</span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Upload multiple images (JPEG, PNG)...
            </p>
          </div>

          {/*Itinerary Section */}
          <div>
            <label className="block font-semibold mb-1">Add Itinerary</label>
            {itinerary.map((info, index) => (
              <div
                key={index}
                className="border-2 rounded outline-primaryColor/30 flex flex-col px-4 mt-4 bg-white shadow-sm relative"
              >
                {/* Header with section number and remove button */}
                <div className="flex justify-between items-center pt-4 px-4">
                  <h4 className="font-semibold text-md text-primaryColor">
                    Itinerary {index + 1}
                  </h4>
                  {itinerary.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeItinerary(index)}
                      className="text-sm text-red-500 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="border-b-2 outline-primaryColor/30 flex flex-col px-4 py-4 gap-y-3">
                  {/*Day */}
                  <div>
                    <label className="block font-medium text-sm mb-1">
                      Day
                    </label>
                    <input
                      type="text"
                      name="day"
                      value={info.day}
                      placeholder={`Day ${index + 1}`}
                      onChange={(e) => handleItineraryChange(e, index)}
                      className="w-full px-3 py-2 border-2 rounded outline-primaryColor/30"
                    />
                  </div>
                  {/* Heading Input */}
                  <div>
                    <label className="block font-medium text-sm mb-1">
                      Heading
                    </label>
                    <input
                      type="text"
                      name="heading"
                      value={info.heading}
                      onChange={(e) => handleItineraryChange(e, index)}
                      className="w-full px-3 py-2 border-2 rounded outline-primaryColor/30"
                    />
                  </div>

                  {/* Content Input */}
                  <div>
                    <label className="block font-medium text-sm mb-1">
                      Content
                    </label>
                    <ReactQuill
                      value={info.content}
                      onChange={(value) => {
                        setItinerary((prev) =>
                          prev.map((item, i) =>
                            i === index ? { ...item, content: value } : item,
                          ),
                        );
                      }}
                      className="bg-white custom-quill"
                    />
                  </div>
                  {/* Image Upload */}
                  <div>
                    <label className="block font-medium text-sm mb-1">
                      Campaign Images{" "}
                      <span className="text-xs text-amber-600">
                        {" "}
                        (Max size 10mb)
                      </span>
                    </label>
                    <div className="flex items-start gap-4">
                      <button
                        type="button"
                        onClick={() =>
                          document
                            .getElementById(`image-upload-itinerary-${index}`)
                            ?.click()
                        }
                        className="flex flex-col items-center justify-center w-24 h-16 border-2 border-dashed border-gray-300 rounded-lg hover:border-primaryColor transition-colors"
                      >
                        <FaFileImage className="text-xl text-gray-400 mb-2" />
                        <span className="text-xs text-gray-500">
                          {info.infoImages.length > 0
                            ? "Add More"
                            : "Upload Images"}
                        </span>
                      </button>

                      <input
                        id={`image-upload-itinerary-${index}`}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => handleItineraryImageChange(e, index)}
                        className="hidden"
                      />

                      {/* Image Previews */}
                      {info.infoImages.length > 0 && (
                        <div className="flex-1 overflow-x-auto pb-2">
                          <div className="flex gap-3">
                            {info.infoImages.map((img, imgIndex) => (
                              <div
                                key={img.localId || img.url || imgIndex}
                                className="relative flex-shrink-0 w-24 h-16"
                              >
                                <img
                                  src={img.preview || img.url || img}
                                  alt={`Upload ${imgIndex}`}
                                  className="w-full h-full object-cover rounded border"
                                />
                                {img.isUploading && (
                                  <div className="absolute inset-0 bg-black bg-opacity-40 flex justify-center items-center z-10">
                                    <FaSpinner className="text-white animate-spin" />
                                  </div>
                                )}
                                {/* REMOVE BUTTON */}
                                <button
                                  type="button"
                                  onClick={() =>
                                    removeItineraryImage(index, imgIndex)
                                  }
                                  className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-5 h-5 pb-0.5 flex items-center justify-center shadow-md hover:bg-red-700 transition-colors"
                                >
                                  <span>x</span>
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-[0.6rem] px-1 py-1 text-gray-500">
                      Upload multiple images
                    </p>
                  </div>

                  {/* VIDEO URL SECTION */}
                  <div>
                    <label className="block font-medium text-sm mb-1">
                      Video URLs
                    </label>

                    {info.videoURL.map((vid, vidIndex) => (
                      <div key={vidIndex} className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={vid}
                          onChange={(e) =>
                            handleVideoChange(e, index, vidIndex)
                          }
                          placeholder="Enter video URL"
                          className="w-full px-3 py-2 border rounded"
                        />

                        {info.videoURL.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeVideoField(index, vidIndex)}
                            className="text-red-500"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={() => addVideoField(index)}
                      className="text-sm text-primaryColor"
                    >
                      + Add Video
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {/* Add More Description Button */}
            <button
              onClick={addMoreItinerary}
              type="button"
              className="font-semibold text-primaryColor text-right py-2 pt-6 px-2"
            >
              + Add more Itinerary
            </button>
          </div>

          {/* BOTTOM BUTTONS */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded border border-gray-400 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`px-4 py-2 bg-green-600 ${
                isEditMode ? "w-40" : "w-20"
              }  text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed`}
              disabled={isSubmitting || anyImageUploading}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (
                <FaSpinner className="text-white animate-spin mx-auto" />
              ) : isEditMode ? (
                "Save Changes"
              ) : (
                "Add"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

export default SpecialCampaignModal;
